import Head from 'next/head';
import getConfig from 'next/config';
import { useRouter } from 'next/router';

import Layout from '../../../components/Layout';
import Hero from '../../../components/Home/Hero';
import BoxHeading from '../../../components/UI/BoxHeading';
import SquarePattern from '../../../components/UI/SquarePattern';
import MasonryCard from '../../../components/UI/MasonryCard';
import Testimonial from '../../../components/UI/Testimonial';
import StatsWithIcons from '../../../components/UI/StatsWithIcons';
import Calculator from '../../../components/DemandLP/SalaryCalculator/Calculator';

import metaRobots from '../../../helpers/metaRobots';

import sharecardImage from '../../../public/images/sharecard.jpg';
import { Box, Container, Grid, GridItem } from '@chakra-ui/react';
import sitePath from '../../../helpers/sitePath';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

const { publicRuntimeConfig } = getConfig();
const { talentHubOnboardingUrl } = publicRuntimeConfig;

const hero = {
  longText: true,
  smallText: true,
  title: {
    formatted:
      'Hire developers in Poland for <span style="color: #96BA6E">top engineering talent</span>',
    main: '',
  },
  text: 'Poland is a tech hotspot with both experienced developers and high gender diversity, making it a prime choice for growing your team. Hire elite developers, mobile app developers, QA engineers, and more in Poland 40% faster with Terminal.',
  image: {
    sourceUrl: '/images/hiring-markets/poland.svg',
    altText: 'Canadian buildings represent Terminal hiring markets',
  },
  button: {
    url: talentHubOnboardingUrl,
    text: 'Browse Talent',
    width: '15rem',
  },
};
const boxHeading = {
  title:
    'With talented, senior engineers and a dynamic startup marketplace, Poland is officially on the map as a hiring destination for fast-growing US startups.',
  text: 'This European country is making major investments into its tech industry, with government acceleration programs and a fast-track visa program for immigrant engineers. With high talent availability, quality and English proficiency, it’s the perfect hiring market for your tech team.',
  color: 'grey.900',
};
const masonryCards = [
  {
    type: 'content_image',
    position: true,
    bgDark: true,
    image: {
      sourceUrl: '/images/hiring-markets/time-zone-alignment-poland.svg',
      altText: 'Polish engineer collaborates virtually with Chilean engineer',
    },
    title: 'Achievable time zone alignment for productivity boosts',
    text: [
      {
        strong: '',
        normal:
          'While Poland may not offer the same time zone alignment as Canada or Latin America, it still offers 3-5 hours of team overlap with US-based teams. Many engineers in Poland have experience working with US companies, and excel in proactive remote working styles.',
      },
    ],
  },
  {
    type: 'content_image',
    position: false,
    bgDark: false,
    image: {
      sourceUrl: '/images/hiring-markets/why-terminal.jpg',
      altText: 'A computer screen with remote engineers in a Zoom meeting',
    },
    title: 'High-powered engineering teams, fully remote',
    text: [
      {
        strong: '',
        normal:
          'Terminal helps you find fully remote tech talent to build your engineering teams. Then, we go beyond recruiting – as the Employer of Record, we take care of all paperwork and legal red tape, onboarding and ongoing admin services, and offer expert localized HR. As part of their comprehensive benefits packages, engineers receive workspace budgets to enhance their home offices for optimal remote productivity.',
      },
    ],
  },
  {
    type: 'content_image',
    position: true,
    bgDark: false,
    image: {
      sourceUrl: '/images/hiring-markets/countries-with-background.svg',
      altText: 'Four engineers from Canada, Mexico, Chile and Colombia',
    },
    title: 'Tapping into tech talent around the world',
    text: [
      {
        strong: '',
        normal:
          'Along with our newest markets in Poland, Terminal’s recruiting network extends to tech hotspots in Spain, Canada, Chile, Colombia, Costa Rica and Mexico. With a multi-market hiring approach, you’ll rapidly scale your engineering team and gain access to a diverse set of perspectives to fuel innovation for your next big project.',
      },
    ],
  },
];
const statsTitle = 'Quality meets quantity in this booming developer talent pool';
const stats = [
  {
    image: {
      sourceUrl: '/images/icons/icon-human-plus-blue.svg',
      altText: 'Engineer illustration',
    },
    item: '160k +',
    text: 'engineers and related technical professionals',
  },
  {
    image: {
      sourceUrl: '/images/icons/icon-chat.svg',
      altText: 'Chat bubbles illustration',
    },
    item: '16th',
    text: 'globally in English proficiency',
  },
  {
    image: {
      sourceUrl: '/images/icons/icon-briefcase.svg',
      altText: 'Briefcase illustration',
    },
    item: '8.5 years',
    text: 'experience in tech and development',
  },
];

const testimonial = {
  bgDark: true,
  image: {
    sourceUrl: '/images/testimonials/david-wiesen.jpg',
    altText: 'David Wiesen',
  },
  text: '“Working with Terminal helped us do everything, not just faster, but certainly faster and better. When you work with a company that has expertise in that market and country, you get things right off the bat. We get an HR team who knows about the hiring laws and benefits laws. And we get a recruiter who already has a network.”',
  author: {
    name: 'David Wiesen',
    jobTitle: 'Co-Founder + Director of Engineering, Nextdoor',
  },
};

const calculatorTheme = {
  green100: 'green.100',
  green200: 'green.500',
  green300: 'grey.900',
};

const footer = {
  longText: false,
  text: 'Ready to start building with global talent?',
};

export async function getServerSideProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale)),
    },
  };
}

export default function Canada() {
  const router = useRouter();

  const meta = {
    title: 'Hire Remote Developers in Poland - Terminal.io',
    description:
      'Hire elite remote software developers in Poland with Terminal. We source, vet, and manage payroll & benefits for full-time & contract engineers in Poland.',
  };

  return (
    <>
      <Head>
        <link rel="canonical" href={sitePath(router.asPath)} />
        <title>{meta.title}</title>
        <meta property="og:title" content={meta.title} />
        <meta name="description" content={meta.description} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={sitePath(router.asPath)} />
        <meta property="og:image" content={sharecardImage.src} />
        <meta name="robots" content={metaRobots({ translated: false, locale: router.locale })} />
      </Head>
      <Layout type="businesses" footer={footer} withCTA>
        {hero && (
          <Box bgColor="grey.100">
            <Hero hero={hero} />
          </Box>
        )}
        <SquarePattern
          mt={{ base: '0.25rem', lg: '0.75rem' }}
          mb={{ base: '-18.4375rem', lg: '-14.25rem' }}
        />
        {boxHeading && (
          <BoxHeading
            title={boxHeading.title}
            text={boxHeading.text}
            color={boxHeading.color}
            maxW="48.75rem"
          />
        )}
        {masonryCards && masonryCards.length && (
          <>
            <Grid gap={{ base: '14', lg: '6' }}>
              {masonryCards.slice(0, 1).map((card) => (
                <GridItem key={card.title}>
                  <MasonryCard card={card} />
                </GridItem>
              ))}
            </Grid>
          </>
        )}
        {stats && stats.length && <StatsWithIcons stats={stats} title={statsTitle} />}
        {masonryCards && masonryCards.length && (
          <>
            <Grid gap={{ base: '14', lg: '6' }}>
              {masonryCards.slice(1, 2).map((card) => (
                <GridItem key={card.title}>
                  <MasonryCard card={card} />
                </GridItem>
              ))}
            </Grid>
            <SquarePattern mt={{ base: '-18.75rem', lg: '-14.5rem' }} />
          </>
        )}
        {masonryCards && masonryCards.length && (
          <>
            <Grid gap={{ base: '14', lg: '6' }}>
              {masonryCards.slice(2, 3).map((card) => (
                <GridItem key={card.title}>
                  <MasonryCard card={card} />
                </GridItem>
              ))}
            </Grid>
          </>
        )}
        {testimonial && (
          <>
            <Container mt={{ base: '5' }} mb={{ base: '5' }}>
              <Testimonial
                testimonial={testimonial}
                pt="16"
                pb="20"
                px={{ base: '16', lg: '20' }}
              />
            </Container>
            <SquarePattern mt={{ base: '-18.75rem', lg: '-14.5rem' }} />
          </>
        )}
        <Box mt={{ base: '10', lg: '24' }}>
          <Calculator theme={calculatorTheme} withStart={false} rounded={false} />
        </Box>
      </Layout>
    </>
  );
}
